"use client";

/**
 * Indicador de progreso de sincronización offline en el topbar.
 *
 * - Muestra un pill compacto con icono + texto:
 *     - Syncing: spinner ámbar + "NN%" o "Sincronizando…"
 *     - Done   : check verde + "Listo" (auto-oculta tras 6s)
 *     - Error  : triángulo rojo + "Error"
 * - Click abre popover con barra por dominio (productores, siembras, etc.)
 *
 * Se alimenta de `lib/offline/sync-progress.ts`. Si no hay sync activo
 * ni recién terminado, no renderiza nada.
 */

import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  RefreshCw,
  X,
} from "lucide-react";
import {
  clearFinished,
  type DomainProgress,
  getProgressSync,
  listenProgress,
  loadProgress,
} from "@/lib/offline/sync-progress";

const AUTO_HIDE_AFTER_DONE_MS = 6_000;

export default function SyncProgress() {
  const [items, setItems] = useState<DomainProgress[]>([]);
  const [open, setOpen] = useState(false);
  const [hiddenAfterDone, setHiddenAfterDone] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Hidratar desde IDB al montar y suscribirse a cambios.
  useEffect(() => {
    let cancelled = false;
    loadProgress().then((list) => {
      if (!cancelled) setItems(list);
    });
    const unsub = listenProgress(() => {
      setItems(getProgressSync());
      setHiddenAfterDone(false); // reaparece si llega algo nuevo
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  // Auto-ocultar cuando todo terminó OK.
  useEffect(() => {
    if (items.length === 0) return;
    const anyActive = items.some((i) => i.status === "syncing");
    const anyError = items.some((i) => i.status === "error");
    if (anyActive || anyError) return;
    const t = setTimeout(() => setHiddenAfterDone(true), AUTO_HIDE_AFTER_DONE_MS);
    return () => clearTimeout(t);
  }, [items]);

  // Cerrar popover al click fuera.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (items.length === 0) return null;
  if (hiddenAfterDone) return null;

  const syncing = items.filter((i) => i.status === "syncing");
  const errored = items.filter((i) => i.status === "error");
  const done = items.filter((i) => i.status === "done");

  // Progreso global: suma de done sobre suma de totals conocidos.
  const totalKnown = items.reduce(
    (acc, i) => acc + (i.total > 0 ? i.total : i.done),
    0,
  );
  const doneSum = items.reduce((acc, i) => acc + i.done, 0);
  const pct = totalKnown > 0 ? Math.min(100, Math.round((doneSum / totalKnown) * 100)) : 0;

  let icon: React.ReactNode;
  let color: string;
  let label: string;

  if (syncing.length > 0) {
    icon = <RefreshCw size={12} className="animate-spin" />;
    color = "text-amber-400";
    label = totalKnown > 0 ? `${pct}%` : "…";
  } else if (errored.length > 0) {
    icon = <AlertTriangle size={12} />;
    color = "text-red-400";
    label = "Error";
  } else {
    // Todos done.
    icon = <CheckCircle2 size={12} />;
    color = "text-green-400";
    label = "Listo";
  }

  return (
    <div className="relative" ref={popupRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={
          syncing.length > 0
            ? `Sincronizando ${syncing.map((i) => i.label).join(", ")}`
            : errored.length > 0
              ? "Error de sincronización"
              : "Sincronización completada"
        }
        className={`inline-flex items-center gap-1 px-1.5 py-1 rounded-md text-[10px] font-medium hover:bg-surface-hover transition ${color}`}
        aria-label="Progreso de sincronización"
      >
        {icon}
        <span>{label}</span>
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30 sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="
              z-50 rounded-xl border border-border bg-surface shadow-xl p-3 space-y-3
              fixed left-1/2 -translate-x-1/2 top-14 w-[calc(100vw-1rem)] max-w-sm
              sm:fixed sm:right-4 sm:top-14 sm:left-auto sm:translate-x-0 sm:w-80
            "
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Download size={14} className="text-text-muted" />
                <p className="text-xs font-semibold text-text">
                  Sincronización offline
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-text-muted hover:text-text"
                aria-label="Cerrar"
              >
                <X size={14} />
              </button>
            </div>

            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {items.map((it) => {
                const itemPct =
                  it.total > 0
                    ? Math.min(100, Math.round((it.done / it.total) * 100))
                    : it.status === "done"
                      ? 100
                      : 0;
                const barColor =
                  it.status === "error"
                    ? "bg-red-400"
                    : it.status === "done"
                      ? "bg-green-400"
                      : "bg-amber-400";
                const statusLabel =
                  it.status === "done"
                    ? `✓ ${it.done.toLocaleString("es-DO")}`
                    : it.status === "error"
                      ? "Error"
                      : it.total > 0
                        ? `${it.done.toLocaleString("es-DO")} / ${it.total.toLocaleString("es-DO")}`
                        : `${it.done.toLocaleString("es-DO")}…`;
                return (
                  <div key={it.domain} className="space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-text font-medium">{it.label}</span>
                      <span className="text-text-muted tabular-nums">
                        {statusLabel}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-surface-hover overflow-hidden">
                      <div
                        className={`h-full transition-[width] duration-300 ${barColor}`}
                        style={{ width: `${itemPct}%` }}
                      />
                    </div>
                    {it.error && (
                      <p className="text-[10px] text-red-400 line-clamp-2">
                        {it.error}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {syncing.length === 0 && (done.length > 0 || errored.length > 0) && (
              <button
                type="button"
                onClick={() => {
                  clearFinished();
                  setOpen(false);
                }}
                className="w-full rounded-lg border border-border bg-surface-hover hover:bg-surface-hover/70 px-3 py-1.5 text-[11px] font-medium text-text-muted transition"
              >
                Ocultar
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
